import React, { useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Modal, IconButton, PrimaryButton, Spinner } from '@fluentui/react';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

const getStyles = () => ({
  bookingContainer: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  header: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '& button:hover': {
      color: 'black',
    },
  },
  title: {
    height: 84,
    lineHeight: '84px',
    fontSize: 24,
    fontWeight: 600,
    marginTop: 0,
    marginBottom: 0,
  },
  cancelIcon: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 1,
  },
  footer: {
    height: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  content: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  footerBtn: {
    borderRadius: 100,
    height: 48,
    padding: '15px 34px 15px 34px',
    fontSize: 20,
    lineHeight: '24px',
    backgroundColor: 'black',
    border: '1px solid black',
    '&:hover': {
      backgroundColor: 'black',
      border: '1px solid black',
    },
    '&[disabled]': {
      backgroundColor: 'lightgray',
      border: '1px solid lightgray',
      color: 'darkgray',
    },
  },
  loadingMask: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    background: 'gray',
    zIndex: 2,
    opacity: '0.4',
  },
});

const modalDefaultStyle = {
  main: {
    width: '1052px',
    height: '720px',
  },
  scrollableContent: {
    maxHeight: 'unset',
    height: '100%',
  },
};

const iconButtonStyles = {
  root: {
    marginLeft: 'auto',
    marginTop: '4px',
    marginRight: '2px',
    color: 'black',
  },
};

const cancelIcon = { iconName: 'Cancel' };

export const UnStyledSmartBookingModal = ({
  children,
  i18n,
  classes,
  className,
  isOpen,
  title,
  isLoading,
  actionButtonLabel,
  onCloseModal,
  onClickFooterButton,
  modalStyle,
  hideFooter,
  hideHeader,
  onRenderHeader,
}) => {
  const mergedStyles = useMemo(() => ({
    main: [modalDefaultStyle.main, modalStyle.main],
    scrollableContent: [modalDefaultStyle.scrollableContent, modalStyle.scrollableContent],
    ..._.omit(modalStyle, 'main', 'scrollableContent'),
  }), [modalStyle]);

  const header = useMemo(() => {
    if (hideHeader) {
      return null;
    }

    if (_.isFunction(onRenderHeader)) {
      return onRenderHeader();
    }

    return <h2 className={classNames(classes.title, 'title')}>{title}</h2>;
  }, [classes, hideHeader, onRenderHeader, title]);

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onCloseModal}
      styles={mergedStyles}
      className={classNames('booking-modal', className)}
    >
      <div className={classes.bookingContainer}>
        {
          isLoading && (
            <div className={classes.loadingMask}>
              <Spinner />
            </div>
          )
        }
        <div className={classNames(classes.header, 'header')}>
          { header }
          <IconButton
            className={classNames(classes.cancelIcon, 'close-button')}
            styles={iconButtonStyles}
            iconProps={cancelIcon}
            ariaLabel={i18n.getString(_TL_('Close popup modal'))}
            onClick={onCloseModal}
          />
        </div>
        <div className={classNames(classes.content, 'content')}>
          {children}
        </div>
        {
          !hideFooter && (
            <div className={classNames(classes.footer, 'footer')}>
              <PrimaryButton
                className={classes.footerBtn}
                text={actionButtonLabel}
                onClick={onClickFooterButton}
              />
            </div>
          )
        }
      </div>
    </Modal>
  );
};

UnStyledSmartBookingModal.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({
    header: PropTypes.string.isRequired,
    footer: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
  }).isRequired,
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  actionButtonLabel: PropTypes.string,
  onCloseModal: PropTypes.func.isRequired,
  onClickFooterButton: PropTypes.func,
  children: PropTypes.node.isRequired,
  modalStyle: PropTypes.shape({
    main: PropTypes.shape({
      width: PropTypes.string,
      height: PropTypes.string,
    }),
  }),
  hideFooter: PropTypes.bool,
  hideHeader: PropTypes.bool,
  onRenderHeader: PropTypes.func,
};

UnStyledSmartBookingModal.defaultProps = {
  modalStyle: {},
  className: '',
  title: '',
  actionButtonLabel: '',
  isLoading: false,
  hideFooter: false,
  hideHeader: false,
  onClickFooterButton: _.noop(),
  onRenderHeader: null,
};

export const SmartBookingModal = withDefaultStyles(
  UnStyledSmartBookingModal,
  getStyles
);
